import { ref, unref, type Ref } from 'vue'

/** vee-validate form errors, keyed by field path (e.g. `address.postcode`). */
export type FieldErrors = Partial<Record<string, string>>

type BindFieldPropsOptions = {
  fieldName: string
  formSelector: string
  getFieldErrors: () => FieldErrors | undefined
  validateField: (fieldName: string) => Promise<{ valid: boolean }>
  onBlur?: (event: Event) => void | Promise<void>
  attempt?: Ref<number>
  bumpAttempt?: () => void
}

const hasFieldError = (fieldErrors: FieldErrors | undefined, fieldName: string | null) =>
  Boolean(
    fieldErrors &&
    fieldName &&
    (fieldErrors[fieldName] || Object.keys(fieldErrors).some((key) => key.endsWith(`.${fieldName}`))),
  )

const resolveTargetField = (formSelector: string, fieldName?: string) => {
  const normalizedFieldName = fieldName?.split('.').at(-1)
  const candidate = [fieldName, normalizedFieldName].find((name): name is string =>
    Boolean(name && document.querySelector(`${formSelector} [name="${name}"]`)),
  )

  return candidate || fieldName
}

const getLastInvalidField = (formSelector: string, fieldErrors: FieldErrors | undefined) => {
  return Array.from(document.querySelectorAll<HTMLElement>(`${formSelector} [name]`))
    .map((field) => field.getAttribute('name'))
    .filter((name): name is string => hasFieldError(fieldErrors, name))
    .at(-1)
}

export const scrollToFieldOrLastInvalid = (
  formSelector: string,
  fieldErrors: FieldErrors | undefined,
  fieldName?: string,
) => {
  const targetField = resolveTargetField(formSelector, fieldName) || getLastInvalidField(formSelector, fieldErrors)
  if (targetField) scrollToError(document.querySelector(`${formSelector} [name="${targetField}"]`))
}

export const validateOnBlur = async (
  validator: () => Promise<{ valid: boolean }>,
  fieldName: string,
  formSelector: string,
  fieldErrors: FieldErrors | undefined,
) => {
  const { valid } = await validator()
  scrollToFieldOrLastInvalid(formSelector, fieldErrors, valid ? undefined : fieldName)
}

export const createScrollToInvalidField = (formSelector: string, getFieldErrors: () => FieldErrors | undefined) => {
  return (fieldName?: string) => scrollToFieldOrLastInvalid(formSelector, getFieldErrors(), fieldName)
}

export const createFormValidationHelpers = <TFieldName extends string>(config: {
  formSelector: string
  getFieldErrors: () => FieldErrors | undefined
  validateField: (fieldName: string) => Promise<{ valid: boolean }>
}) => {
  // Shared across all fields in the form so submitting or re-blurring re-flashes an unchanged error.
  const attempt = ref(0)
  const bumpAttempt = () => {
    attempt.value++
  }

  return {
    scrollToInvalidField: createScrollToInvalidField(config.formSelector, config.getFieldErrors),
    attempt,
    bumpAttempt,
    getBindFieldOptions: (
      fieldName: TFieldName,
      onBlur?: (event: Event) => void | Promise<void>,
    ): BindFieldPropsOptions => ({
      fieldName,
      formSelector: config.formSelector,
      getFieldErrors: config.getFieldErrors,
      validateField: config.validateField,
      onBlur,
      attempt,
      bumpAttempt,
    }),
  }
}

export const bindFieldProps = (fieldProps: unknown, options: BindFieldPropsOptions) => {
  const { onBlur, ...props } = unref(fieldProps) as Record<string, unknown>
  const originalOnBlur = onBlur as ((event: Event) => void | Promise<void>) | undefined

  return {
    ...props,
    attempt: options.attempt?.value,
    onBlur: async (event: Event) => {
      await originalOnBlur?.(event)
      options.bumpAttempt?.()
      const { valid } = await options.validateField(options.fieldName)
      scrollToFieldOrLastInvalid(options.formSelector, options.getFieldErrors(), valid ? undefined : options.fieldName)
      await options.onBlur?.(event)
    },
  }
}
